import { HostError } from './host';
import {
  BUSINESS_PERMISSION_CAPABILITIES,
  type BusinessDepartment,
  type BusinessDepartmentMember,
  type BusinessDepartmentResource,
  type BusinessResourceGrant,
} from './contracts/business-permissions';

/** Offline fixtures only; production membership and resource ownership are enforced by the server. */
export interface MockBusinessPermissionsSeed {
  scoped?: boolean;
  departments?: Array<
    BusinessDepartment & { members?: BusinessDepartmentMember[]; resources?: BusinessDepartmentResource[] }
  >;
  grants?: BusinessResourceGrant[];
}

export function createMockBusinessPermissions(
  principal: () => { userId?: string; owner: boolean },
  seed: MockBusinessPermissionsSeed = {},
) {
  let scoped = seed.scoped ?? false;
  const departments = structuredClone(seed.departments ?? []);
  const grants = structuredClone(seed.grants ?? []);
  let sequence = departments.length;
  const denied = () => {
    throw new HostError('capability-denied', 'Business permission access denied');
  };
  const resourceMatches = (
    row: BusinessDepartmentResource,
    payload: { resourceId?: unknown; resourceType?: unknown },
  ) => row.resourceId === payload['resourceId'] && row.resourceType === payload['resourceType'];
  return (operation: string, payload: Record<string, unknown>): unknown => {
    const { userId, owner } = principal();
    if (!userId) denied();
    if (operation === 'businessPermissions.status') return { scoped, owner };
    if (operation === 'businessPermissions.enable') {
      if (!owner) denied();
      scoped = true;
      return { scoped, owner };
    }
    if (!scoped) throw new HostError('conflict', 'Business scoped access is not enabled');
    if (operation === 'businessPermissions.departments.list') {
      return departments
        .filter((dept) => owner || dept.members?.some((member) => member.userId === userId))
        .map(({ id, name }) => ({ id, name }));
    }
    if (operation === 'businessPermissions.departments.create') {
      if (!owner) denied();
      if (typeof payload['name'] !== 'string' || !payload['name'].trim() || payload['name'].length > 100)
        throw new HostError('invalid-payload', 'Invalid department name');
      const department = {
        id: `00000000-0000-4000-8000-${String(++sequence).padStart(12, '0')}`,
        name: payload['name'].trim(),
        members: [],
        resources: [],
      };
      departments.push(department);
      return { id: department.id, name: department.name };
    }
    if (operation.startsWith('businessPermissions.grants.')) {
      if (!owner && (operation !== 'businessPermissions.grants.list' || payload['userId'] !== userId))
        denied();
      if (operation.endsWith('.list'))
        return structuredClone(grants.filter((grant) => grant.userId === payload['userId']));
      const index = grants.findIndex(
        (grant) => grant.userId === payload['userId'] && resourceMatches(grant, payload),
      );
      if (operation.endsWith('.remove')) {
        if (index >= 0) grants.splice(index, 1);
        return { removed: index >= 0 };
      }
      validateCapabilities(payload);
      const grant = {
        userId: String(payload['userId']),
        resourceType: payload['resourceType'],
        resourceId: String(payload['resourceId']),
        capabilities: payload['capabilities'],
      } as BusinessResourceGrant;
      if (index >= 0) grants[index] = structuredClone(grant);
      else grants.push(structuredClone(grant));
      return structuredClone(grant);
    }
    const department = departments.find((dept) => dept.id === payload['departmentId']);
    if (!department) throw new HostError('invalid-payload', 'Department not found');
    const members = department.members ?? (department.members = []);
    const resources = department.resources ?? (department.resources = []);
    const actor = members.find((member) => member.userId === userId);
    if (operation.startsWith('businessPermissions.members.')) {
      if (!owner && !actor?.isManager) denied();
      if (operation.endsWith('.list')) return structuredClone(members);
      const index = members.findIndex((member) => member.userId === payload['userId']);
      if (!owner && (payload['isManager'] || members[index]?.isManager)) denied();
      if (operation.endsWith('.remove')) {
        if (index >= 0) members.splice(index, 1);
        return { removed: index >= 0 };
      }
      validateCapabilities(payload);
      const capabilities = payload['capabilities'] as BusinessDepartmentMember['capabilities'];
      if (!owner && capabilities.some((capability) => !actor?.capabilities.includes(capability))) denied();
      const member = {
        userId: String(payload['userId']),
        capabilities,
        isManager: (payload['isManager'] as boolean | undefined) ?? members[index]?.isManager ?? false,
      };
      if (index >= 0) members[index] = structuredClone(member);
      else members.push(structuredClone(member));
      return structuredClone(member);
    }
    if (operation === 'businessPermissions.resources.list') {
      if (!owner && !actor) denied();
      return structuredClone(
        resources.filter(
          (resource) =>
            owner ||
            actor?.capabilities.includes(
              resource.resourceType === 'account' ? 'accounts.read' : 'cards.read',
            ) ||
            grants.some(
              (grant) =>
                grant.userId === userId &&
                resourceMatches(grant, resource) &&
                grant.capabilities.includes(
                  resource.resourceType === 'account' ? 'accounts.read' : 'cards.read',
                ),
            ),
        ),
      );
    }
    if (!owner) denied();
    const index = resources.findIndex((resource) => resourceMatches(resource, payload));
    if (operation === 'businessPermissions.resources.remove') {
      if (index >= 0) resources.splice(index, 1);
      return { removed: index >= 0 };
    }
    if (operation === 'businessPermissions.resources.assign') {
      if (
        departments.some(
          (dept) =>
            dept.id !== department.id &&
            dept.resources?.some((resource) => resourceMatches(resource, payload)),
        )
      )
        throw new HostError('conflict', 'Resource belongs to another department');
      if (payload['resourceType'] !== 'account' && payload['resourceType'] !== 'card')
        throw new HostError('invalid-payload', 'Invalid resource type');
      const resource = {
        resourceType: payload['resourceType'],
        resourceId: String(payload['resourceId']),
      } as BusinessDepartmentResource;
      if (index < 0) resources.push(resource);
      return structuredClone(resource);
    }
    throw new HostError('unknown-operation', operation);
  };
}

function validateCapabilities(payload: Record<string, unknown>): void {
  const capabilities = payload['capabilities'];
  if (
    !Array.isArray(capabilities) ||
    capabilities.length > 3 ||
    new Set(capabilities).size !== capabilities.length ||
    capabilities.some((capability) => !BUSINESS_PERMISSION_CAPABILITIES.includes(capability))
  )
    throw new HostError('invalid-payload', 'Invalid business capabilities');
  const type = payload['resourceType'];
  if (
    type !== undefined &&
    ((type !== 'account' && type !== 'card') ||
      capabilities.some((capability) =>
        type === 'account' ? capability !== 'accounts.read' : capability === 'accounts.read',
      ))
  )
    throw new HostError('invalid-payload', 'Capability does not apply to resource');
}
